import React from "react";
import { Box, Grid, FormGroup, FormControlLabel, Checkbox, Radio, RadioGroup, Typography, Divider, TextField } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";

import CustomInput from "../../Components/CustomInput";
import AnimatedBorderButton from "../../Components/AnimatedBorderButton";
import { contactForm } from "../../api/services";
import { Homeimages } from "../../assets";

interface MentorshipData {
  fullName: string;
  email: string;
  phone: string;
  cityState: string;
  communication: string[];
  orgName: string;
  designation: string;
  teamSize: string;
  experience: string[];
  comments?: string;
  type?: string;

  // NEW FIELDS
  projects?: string; // Current Real Estate Project(s) You Are Handling
  ticketSizes?: string[]; // Ticket Size / Price Range per unit
  salesGoal?: string; // Sales Goal / Objective you wish to achieve through training
  services?: string[]; // Select the mentorship services you're interested in
  preferredStartDate?: string; // Preferred Start Date
  additionalNotes?: string; // Additional notes (optional)
}

// keep schema typing compatible with useForm<MentorshipData> by omitting `type`
const schema: yup.ObjectSchema<Omit<MentorshipData, "type">> = yup.object({
  fullName: yup.string().required("Full Name is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  phone: yup
    .string()
    .matches(/^[6-9]\d{9}$/, "Phone number must be valid")
    .required("Phone number is required"),
  cityState: yup.string().required("City & State is required"),

  communication: yup
    .array()
    .of(yup.string().required())
    .min(1, "Select at least one option")
    .required()
    .default([]),

  orgName: yup.string().required("Organization name required"),
  designation: yup.string().required("Designation required"),

  teamSize: yup.string().required("Team Size is required"),

  experience: yup
    .array()
    .of(yup.string().required())
    .min(1, "Select at least one experience level")
    .required()
    .default([]),

  comments: yup.string().optional(),

  // NEW VALIDATION (optional fields)
  projects: yup.string().optional(),
  // ticketSizes: yup.array().of(yup.string()).optional().default([]),
  salesGoal: yup.string().optional(),
  // services: yup.array().of(yup.string()).optional().default([]),
  ticketSizes: yup.array().of(yup.string().required()).optional().default([]),
  services: yup.array().of(yup.string().required()).optional().default([]),

  preferredStartDate: yup.string().optional(),
  additionalNotes: yup.string().optional()
});

const MentorshipForm = () => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm<MentorshipData>({
    resolver: yupResolver(schema),
    defaultValues: {
      communication: [],
      experience: [],
      // NEW DEFAULTS
      projects: "",
      ticketSizes: [],
      salesGoal: "",
      services: [],
      preferredStartDate: "",
      additionalNotes: ""
    }
  });

  const mutation = useMutation({
    // cast to any to avoid external type mismatch
    mutationFn: (newData: MentorshipData) => contactForm(newData as any),
    onSuccess: () => {
      toast.success("Form Submitted Successfully!");
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Form submission failed");
    }
  });

  const resetForm = () => {
    reset({
      fullName: "",
      email: "",
      phone: "",
      cityState: "",
      communication: [],
      orgName: "",
      designation: "",
      teamSize: "",
      experience: [],
      comments: "",
      // reset new fields
      projects: "",
      ticketSizes: [],
      salesGoal: "",
      services: [],
      preferredStartDate: "",
      additionalNotes: ""
    });
  };

  const onSubmit = (data: MentorshipData) => {
    mutation.mutate({ ...data, type: "mentorship" });
  };

  const communicationValues = watch("communication");
  const experienceValues = watch("experience");
  const ticketSizeValues = watch("ticketSizes") || [];
  const servicesValues = watch("services") || [];

  // services array (fixed - previously used an object literal)
  const SERVICES: string[] = [
    "Real Estate Growth Accelerator (5+1 - Sessions Program)",
    "The Plot Closing Framework (5+1 - Sessions Program)",
    "Luxury Sales Mastery (5+1 - Sessions Program)",
    "Uber Luxury Sales Mastery (7+1-Sessions Intensive)",
    "Organizational Mentoring (Long-Term Engagement / Retainer)",
    "Exclusive Personalized Mentorship for MD's, CXO's, Board Members (Upon Request)"
  ];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // backgroundColor: "#f9f9f9", // light background
        py: 6,
      }}
    >

      <Box component="form" maxWidth="md" onSubmit={handleSubmit(onSubmit)} >
        <Grid container spacing={3} >
          {/* Title */}
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="flex-start"
              sx={{ maxWidth: 900, mx: "auto" }}
            >
              {/* Left side: Logo */}
              <Grid item xs={12} md={3} display="flex" justifyContent="center">
                <Box
                  component="img"
                  src={Homeimages.rwbluelogo2} // replace with your logo path
                  alt="Rajiv Williams Logo"
                  sx={{
                    width: { xs: 100, md: 140 },
                    height: "auto",
                    objectFit: "contain",
                  }}
                />
              </Grid>

              {/* Right side: Heading + Description + Divider */}
              <Grid item xs={12} md={9}>
                <Box textAlign="start">
                  {/* Heading with underline */}
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{
                      display: "inline-block",
                      borderBottom: "3px solid #000", // underline color
                      // pb: 0.5,
                      mb: 1,
                    }}
                  >
                    Real Estate Mentorship by Rajiv Williams
                  </Typography>

                  {/* Paragraph */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.primary",
                      lineHeight: 1.6,
                      mt: 1,
                    }}
                  >
                    Unlock your potential in real estate. Whether you're starting your
                    journey or scaling, let's tailor a mentorship program just for you.
                  </Typography>

                  {/* Horizontal divider below */}
                  <Divider
                    sx={{
                      mt: 2,
                      borderColor: "#000", // black color
                      borderBottomWidth: "1px", // thicker line
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>






        <Grid container spacing={2} >

          <Box>
            {/* Personal Information */}
            <Grid item xs={12} mt={4}>
              <Typography variant="h6" sx={{ textAlign: 'start', }} fontWeight={600}>PERSONAL INFORMATION</Typography>
            </Grid>
            {/* Full Name */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="end" >
                {/* <Typography  sx={{ fontWeight: 'bold',fontSize:'10px'  }}>Full Name:</Typography> */}

                <Typography
                  variant="body2"
                  style={{ fontWeight: 'bold', }}
                >
                  Full Name:
                </Typography>
                <Box flex={1}>
                  <Controller
                    name="fullName"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        label=""
                        fullWidth
                        size="small"
                        {...field}
                        error={!!errors.fullName}
                        helperText={errors.fullName?.message}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Email Address */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="end" >
                <Typography sx={{}}>Email Address:</Typography>
                <Box flex={1}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        label=""
                        fullWidth
                        size="small"
                        {...field}
                        error={!!errors.email}
                        helperText={errors.email?.message}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Phone Number */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="end" >
                <Typography sx={{}}>Phone Number:</Typography>
                <Box flex={1}>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        label=""
                        fullWidth
                        size="small"
                        {...field}
                        error={!!errors.phone}
                        helperText={errors.phone?.message}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Grid>


            {/* Preferred Communication */}
            <Grid item xs={12}>
              <Box
                // variant="h6"
                sx={{
                  textAlign: 'start',
                  mt: 2,
                  // fontWeight:'bold',
                  fontSize: '20px'
                }}
              >
                Preferred Mode of Communication:
              </Box>
              <FormGroup row>
                {["Phone Call", "WhatsApp", "Email", "Video Meeting"].map((opt: string) => (
                  <FormControlLabel
                    key={opt}
                    label={opt}
                    control={
                      <Controller
                        name="communication"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            checked={field.value.includes(opt)}
                            onChange={() => {
                              const updated = field.value.includes(opt) ? field.value.filter((v) => v !== opt) : [...field.value, opt];
                              field.onChange(updated);
                            }}
                          />
                        )}
                      />
                    }
                  />
                ))}
              </FormGroup>
              <Typography color="error" variant="caption">
                {errors.communication?.message}
              </Typography>
            </Grid>

            {/* City & State */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="end" >
                <Typography sx={{}}>City & State:</Typography>
                <Box flex={1}>
                  <Controller
                    name="cityState"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        label="City & State"
                        fullWidth
                        size="small"
                        {...field}
                        error={!!errors.cityState}
                        helperText={errors.cityState?.message}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Grid>
          </Box>


        </Grid>

        {/* ORGANIZATION SECTION */}
        <Typography variant="h6" fontWeight={700} mb={2}>
          ORGANIZATION / TEAM DETAILS
        </Typography>

        <Grid container spacing={2}>
          {/* Organization Name */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="end" >
              <Typography sx={{}}>
                Organization / Developer Name:
              </Typography>
              <Box flex={1}>
                <Controller
                  name="orgName"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      label="Organization / Developer Name"
                      fullWidth
                      size="small"
                      {...field}
                      error={!!errors.orgName}
                      helperText={errors.orgName?.message}
                    />
                  )}
                />
              </Box>
            </Box>
          </Grid>

          {/* Role / Designation */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="end " >
              <Typography sx={{}}>Your Role / Designation:</Typography>
              <Box flex={1}>
                <Controller
                  name="designation"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      label="Your Role / Designation"
                      fullWidth
                      size="small"
                      {...field}
                      error={!!errors.designation}
                      helperText={errors.designation?.message}
                    />
                  )}
                />
              </Box>
            </Box>
          </Grid>

          {/* Team Size */}
          <Grid item xs={12}>
            <Typography>Team Size / No. of Attendees for Mentorship:</Typography>
            <Controller
              name="teamSize"
              control={control}
              render={({ field }) => (
                <RadioGroup row {...field}>
                  <FormControlLabel value="<10" control={<Radio />} label="< 10" />
                  <FormControlLabel value="<20" control={<Radio />} label="< 20" />
                  <FormControlLabel value="20+" control={<Radio />} label="20+" />
                </RadioGroup>
              )}
            />
            <Typography color="error" variant="caption">
              {errors.teamSize?.message}
            </Typography>
          </Grid>

          {/* Experience Level */}
          <Grid item xs={12}>
            <Typography>Experience Level of Participants:</Typography>
            <FormGroup>
              {[
                "Beginner (0–1 year)",
                "Intermediate (2–4 years)",
                "Advanced / Leadership (5+ years)",
              ].map((opt: string) => (
                <FormControlLabel
                  key={opt}
                  label={opt}
                  control={
                    <Controller
                      name="experience"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          checked={field.value.includes(opt)}
                          onChange={() => {
                            const updated = field.value.includes(opt)
                              ? field.value.filter((v) => v !== opt)
                              : [...field.value, opt];
                            field.onChange(updated);
                          }}
                        />
                      )}
                    />
                  }
                />
              ))}
            </FormGroup>
            <Typography color="error" variant="caption">
              {errors.experience?.message}
            </Typography>
          </Grid>

          {/* Comments */}
          <Grid item xs={12}>
            <Typography>Comments - Provide Skill Variance Matrix:</Typography>
            <Controller
              name="comments"
              control={control}
              render={({ field }) => (
                <CustomInput
                  label="Comments - Provide Skill Variance Matrix"
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  {...field}
                />
              )}
            />
          </Grid>

          {/* PROJECT INFORMATION - NEW SECTION */}
          <Grid item xs={12} mt={2}>
            <Typography variant="h6" sx={{ textAlign: "start" }} fontWeight={600}>PROJECT INFORMATION</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography>Current Real Estate Project(s) You Are Handling:</Typography>
            <Controller
              name="projects"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  placeholder=""
                  multiline
                  rows={3}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography sx={{ mt: 1 }}>Ticket Size / Price Range of Project (Per Unit):</Typography>
            <FormGroup row>
              {["< 5 Cr", "< 10 Cr", "< 20 Cr"].map((opt: string) => (
                <FormControlLabel
                  key={opt}
                  label={opt}
                  control={
                    <Controller
                      name="ticketSizes"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          checked={(field.value || []).includes(opt)}
                          onChange={() => {
                            const current = field.value || [];
                            const updated = current.includes(opt) ? current.filter((v: string) => v !== opt) : [...current, opt];
                            field.onChange(updated);
                          }}
                        />
                      )}
                    />
                  }
                />
              ))}
            </FormGroup>
            <Typography color="error" variant="caption">
              {errors.ticketSizes?.message}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography>Sales Goal / Objective You Wish to Achieve through this Training:</Typography>
            <Controller
              name="salesGoal"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                />
              )}
            />
          </Grid>

          {/* MENTORSHIP SERVICES - NEW SECTION */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600 }}>SELECT THE MENTORSHIP SERVICE(S) YOU'RE INTERESTED IN</Typography>
            <FormGroup>
              {SERVICES.map((opt: string) => (
                <FormControlLabel
                  key={opt}
                  label={opt}
                  control={
                    <Controller
                      name="services"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          checked={(field.value || []).includes(opt)}
                          onChange={() => {
                            const current = field.value || [];
                            const updated = current.includes(opt) ? current.filter((v: string) => v !== opt) : [...current, opt];
                            field.onChange(updated);
                          }}
                        />
                      )}
                    />
                  }
                />
              ))}
            </FormGroup>
            <Typography color="error" variant="caption">
              {errors.services?.message}
            </Typography>
          </Grid>

          <Divider sx={{ my: 3, borderColor: "#000", borderBottomWidth: "2px" }} />

          {/* Preferred Start Date (NEW - Availability & Preferences) */}
          <Grid item xs={12} mt={2}>
            <Typography variant="h6" sx={{ textAlign: 'start' }} fontWeight={600}>AVAILABILITY & PREFERENCES</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography>Preferred Start Date:</Typography>
            <Controller
              name="preferredStartDate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          {/* Additional Notes (bottom) */}
          <Grid item xs={12}>
            <Typography>ADDITIONAL NOTES (Optional)</Typography>
            <Controller
              name="additionalNotes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  multiline
                  rows={4}
                />
              )}
            />
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12} mt={5}>
            <AnimatedBorderButton type="submit">Submit</AnimatedBorderButton>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, borderColor: "#000", borderBottomWidth: "2px" }} />




      </Box>
    </Box>
  );
};

export default MentorshipForm;
